import { ReactKeycloakProvider } from "@react-keycloak/web";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./components/css/index.css";
import NoNetworkStatusModal from "./components/element/NoNetworkStatusModal";
import NotificationHandler from "./components/element/NotificationHandler";
import SessionExpiredPopup from "./components/element/SessionExpiredPopup";
import UnauthorizedPopup from "./components/element/UnauthorizedPopup";
import { FormDataProvider } from "./helpers/FormDataProvider";
import PrivateRoute from "./helpers/PrivateRoute";
import { keycloak } from "./Keycloak";
import reportWebVitals from "./reportWebVitals";
import { ConfigurationProvider } from "./services/ConfigurationProvider";
import { ErrorProvider } from "./services/ErrorProvider";
import { LogoutProvider } from "./services/LogoutProvider";
import { ServerDownProvider } from "./services/ServerDownProvider";
import { getServiceUrls } from "./services/serviceUrls";
import { StorageProvider } from "./services/StorageProvider";
import ServerDownHandler from "./components/element/ServerDownHandler";
import { LOCALHOST_HOSTNAMES } from "./utils/constants";

// Validate configurations
const validateConfig = (config) => {
  const requiredKeys = [
    "FEE_SERVICE",
    "USER_SERVICE",
    "TRIP_SERVICE",
    "INVOICE_SERVICE",
    "REACT_APP_KEYCLOAK_URL",
    "REACT_APP_KEYCLOAK_REALM",
    "REACT_APP_KEYCLOAK_CLIENT_ID",
  ];
  requiredKeys.forEach((key) => {
    if (!config[key]) {
      throw new Error(`Missing configuration value for: ${key}`);
    }
  });
};

// Initialize Sentry with dynamic configuration
const initializeSentry = () => {
  const config = getServiceUrls();
  const sentryDsn = config.REACT_APP_SENTRY_DSN;

  // Check if running on localhost to prevent local development errors from being sent to Sentry
  const isLocalhost = LOCALHOST_HOSTNAMES.includes(window.location.hostname) ||
    window.location.hostname.includes('localhost');

  // Only initialize Sentry if not on localhost
  if (!isLocalhost && sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [],
      tracesSampleRate: 1.0,
      autoSessionTracking: true,
      environment: config.REACT_APP_PROFILE_ENV || "dev",
    });
  }
};

// Initialize Sentry early
initializeSentry();

const root = ReactDOM.createRoot(document.getElementById("root"));

const renderApp = () => {
  const queryClient = new QueryClient();
  const keycloakProviderInitConfig = {
    onLoad: "check-sso",
  };
  const kc = keycloak();
   // Check if current path is health check
   const currentPath = window.location.pathname;
   if (currentPath === "/health") {
     // Import and render health check directly without authentication
     import("./components/element/HealthCheck").then(
       ({ default: HealthCheck }) => {
         root.render(<HealthCheck />);
       }
     );
     return;
   }
 

  root.render(
    <ReactKeycloakProvider
      authClient={kc}
      initOptions={keycloakProviderInitConfig}
    >
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <ServerDownProvider>
            <ErrorProvider>
              <LogoutProvider>
                <ServerDownHandler />
                <SessionExpiredPopup />
                <UnauthorizedPopup />
                <NoNetworkStatusModal />
                <FormDataProvider>
                  <ConfigurationProvider >
                    <StorageProvider>
                      <NotificationHandler />
                      <PrivateRoute component={App}></PrivateRoute>
                    </StorageProvider>
                  </ConfigurationProvider>
                </FormDataProvider>
                <ReactQueryDevtools initialIsOpen={false} />
              </LogoutProvider>
            </ErrorProvider>
          </ServerDownProvider>
        </QueryClientProvider>
      </React.StrictMode>
    </ReactKeycloakProvider>
  );
};

try {
  const config = getServiceUrls();
  validateConfig(config);
  renderApp();
} catch (error) {
  console.error("Configuration validation failed:", error.message);
  root.render(
    <div style={{ textAlign: "center", marginTop: "20%" }}>
      <h1>Configuration Error</h1>
      <p>{error.message}</p>
      <p>Please contact the administrator or try refreshing the page.</p>
    </div>
  );
}

reportWebVitals();
